import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, SlidersHorizontal, ArrowUpDown, Loader2 } from 'lucide-react'
import { PageMeta } from '@/components/seo/PageMeta'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { FeatureRequestRow } from '@/components/landing/misc/FeatureRequestRow'
import { CtaBand, blueCtaClass } from '@/components/landing/misc/CtaBand'
import { HeroReveal, StaggerContainer, StaggerItem, Pressable, TextReveal } from '@/components/motion'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/contexts/auth-context'
import {
  featureRequestService,
  categoryLabels,
  statusConfig,
  type FeatureRequestDto,
  type FeatureRequestCategory,
  type FeatureRequestStatus,
} from '@/lib/api/services/featureRequests'

type SortOption = 'votes' | 'newest' | 'oldest'

const categories: FeatureRequestCategory[] = [
  'UiUx',
  'WordAddin',
  'ExcelAddin',
  'PowerPointAddin',
  'OutlookAddin',
  'Api',
  'Integrations',
  'AiAutomation',
  'Workflows',
  'Templates',
  'Other',
]

const publicStatuses: FeatureRequestStatus[] = [
  'Submitted',
  'UnderReview',
  'Planned',
  'InProgress',
  'Completed',
]

export default function FeatureRequests() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [features, setFeatures] = useState<FeatureRequestDto[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<FeatureRequestCategory | 'all'>('all')
  const [selectedStatus, setSelectedStatus] = useState<FeatureRequestStatus | 'all'>('all')
  const [sortBy, setSortBy] = useState<SortOption>('votes')
  const [showFilters, setShowFilters] = useState(false)

  const fetchFeatures = async () => {
    try {
      const response = await featureRequestService.getFeatureRequests()
      setFeatures(response.value)
    } catch (error) {
      console.error('Failed to fetch features:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchFeatures()
  }, [])

  const filteredAndSortedFeatures = useMemo(() => {
    let result = [...features]

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(
        (f) =>
          f.title.toLowerCase().includes(query) ||
          f.description.toLowerCase().includes(query)
      )
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      result = result.filter((f) => f.category === selectedCategory)
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      result = result.filter((f) => f.status === selectedStatus)
    }

    // Sort
    switch (sortBy) {
      case 'votes':
        result.sort((a, b) => b.voteCount - a.voteCount)
        break
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        break
      case 'oldest':
        result.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        break
    }

    return result
  }, [features, searchQuery, selectedCategory, selectedStatus, sortBy])

  const handleSubmitClick = () => {
    if (isAuthenticated) {
      navigate('/feature-requests/submit')
    } else {
      navigate('/login?redirect=/feature-requests/submit')
    }
  }

  const handleLoginRequired = () => {
    navigate('/login?redirect=/feature-requests')
  }

  const filtersActive = searchQuery !== '' || selectedCategory !== 'all' || selectedStatus !== 'all'
  const resetFilters = () => {
    setSearchQuery('')
    setSelectedCategory('all')
    setSelectedStatus('all')
  }

  return (
    <>
      <PageMeta
        title="Feature Requests | Templatean"
        description="Vote on features you'd like to see in Templatean or submit your own ideas. Help shape the future of document automation."
      />

      <LandingLayout>
        {/* Hero: split, bottom-aligned; the submit action sits opposite the title */}
        <section className="landing-container border-b border-[var(--color-landing-gray-900)] pt-16 pb-14 dark:border-white/40 md:pt-24 lg:pb-20">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:items-end lg:gap-24">
            <div>
              <TextReveal
                as="h1"
                lines={['Shape the future of ', <span key="hl" className="landing-highlight landing-highlight--secondary">Templatean.</span>]}
                className="landing-text-hero text-[var(--color-landing-gray-900)] dark:text-white"
              />
              <HeroReveal delay={0.2}>
                <p className="mt-10 max-w-[52ch] border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-lg text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
                  Vote on features you want to see, submit your own ideas, and help us prioritize
                  what to build next. Your voice matters.
                </p>
              </HeroReveal>
            </div>
            <HeroReveal delay={0.3} className="flex flex-col gap-4 lg:items-end">
              <Pressable className="self-start lg:self-end">
                <button type="button" onClick={handleSubmitClick} className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep">
                  <Plus className="h-5 w-5" />
                  Submit a request
                </button>
              </Pressable>
              {!isAuthenticated && (
                <p className="text-sm text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                  You'll be asked to sign in first.
                </p>
              )}
            </HeroReveal>
          </div>
        </section>

        {/* Board: sticky filter column beside the hairline list */}
        <section className="landing-container grid grid-cols-1 gap-10 py-14 lg:grid-cols-[minmax(15rem,20rem)_minmax(0,1fr)] lg:gap-20 lg:py-20">
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <h2 className="sr-only">Filter feature requests</h2>
            <div className="flex items-center justify-between gap-4">
              <span className="-rotate-1 bg-[var(--color-landing-secondary)] px-2.5 py-1 text-sm font-bold uppercase tracking-wide text-white">
                Filter
              </span>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                aria-expanded={showFilters}
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[var(--color-landing-gray-900)] hover:text-[var(--color-landing-accent)] dark:text-white lg:hidden"
              >
                <SlidersHorizontal className="h-4 w-4" />
                {showFilters ? 'Fewer filters' : 'More filters'}
              </button>
            </div>

            <div className="relative mt-5">
              <label htmlFor="feature-search" className="sr-only">Search feature requests</label>
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#6e6e6e] dark:text-[#9a9aa6]" aria-hidden />
              <input
                id="feature-search"
                type="search"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="landing-field pl-12"
              />
            </div>

            <div className={cn('mt-3 flex-col gap-3', showFilters ? 'flex' : 'hidden', 'lg:flex')}>
              <div>
                <label htmlFor="feature-category" className="sr-only">Category</label>
                <select
                  id="feature-category"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value as FeatureRequestCategory | 'all')}
                  className="landing-field"
                >
                  <option value="all">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {categoryLabels[cat]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="feature-status" className="sr-only">Status</label>
                <select
                  id="feature-status"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value as FeatureRequestStatus | 'all')}
                  className="landing-field"
                >
                  <option value="all">All Statuses</option>
                  {publicStatuses.map((status) => (
                    <option key={status} value={status}>
                      {statusConfig[status].label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="feature-sort" className="sr-only">Sort by</label>
                <select
                  id="feature-sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="landing-field"
                >
                  <option value="votes">Most Votes</option>
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>
            </div>

            {filtersActive && (
              <button
                type="button"
                onClick={resetFilters}
                className="mt-4 text-sm font-medium text-[var(--color-landing-gray-900)] underline decoration-[var(--color-landing-gray-300)] underline-offset-4 hover:text-[var(--color-landing-accent)] hover:decoration-[var(--color-landing-accent)] dark:text-white"
              >
                Clear filters
              </button>
            )}
          </aside>

          <div>
            {/* Results count and sort indicator */}
            <div className="flex items-center justify-between gap-4 border-b border-[var(--color-landing-gray-900)] pb-4 text-sm dark:border-white/40">
              <span aria-live="polite" className="font-semibold text-[var(--color-landing-gray-900)] dark:text-white">
                {filteredAndSortedFeatures.length} feature request{filteredAndSortedFeatures.length !== 1 ? 's' : ''}
              </span>
              <button
                type="button"
                onClick={() => setSortBy(sortBy === 'votes' ? 'newest' : 'votes')}
                className="inline-flex items-center gap-1.5 text-[var(--color-landing-gray-600)] transition-colors hover:text-[var(--color-landing-accent)] dark:text-[var(--color-landing-gray-400)] dark:hover:text-white"
              >
                <ArrowUpDown className="h-4 w-4" />
                {sortBy === 'votes' ? 'Sorted by votes' : sortBy === 'newest' ? 'Sorted by newest' : 'Sorted by oldest'}
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-24" role="status">
                <Loader2 className="h-8 w-8 animate-spin text-[var(--color-landing-accent)]" aria-hidden />
                <span className="sr-only">Loading feature requests</span>
              </div>
            ) : filteredAndSortedFeatures.length === 0 ? (
              <div className="mt-10 border border-[var(--color-landing-gray-900)] px-6 py-16 text-center dark:border-white/40 sm:px-12">
                <h3 className="landing-text-display-lg text-[var(--color-landing-gray-900)] dark:text-white">
                  No feature requests found
                </h3>
                <p className="mx-auto mt-4 max-w-md landing-text-body text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                  {filtersActive
                    ? 'Try adjusting your filters or search query.'
                    : 'Be the first to submit a feature request!'}
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-4">
                  {filtersActive && (
                    <button type="button" onClick={resetFilters} className="landing-cta landing-cta--outline landing-cta--md landing-btn-sweep">
                      Reset filters
                    </button>
                  )}
                  <button type="button" onClick={handleSubmitClick} className="landing-cta landing-cta--solid landing-cta--md landing-btn-sweep">
                    <Plus className="h-4 w-4" />
                    Submit the first request
                  </button>
                </div>
              </div>
            ) : (
              <StaggerContainer amount={0.05}>
                {filteredAndSortedFeatures.map((feature) => (
                  <StaggerItem key={feature.id}>
                    <FeatureRequestRow
                      feature={feature}
                      isAuthenticated={isAuthenticated}
                      onVoteChange={fetchFeatures}
                      onLoginRequired={handleLoginRequired}
                    />
                  </StaggerItem>
                ))}
              </StaggerContainer>
            )}
          </div>
        </section>

        <CtaBand
          tone="purple"
          title={
            <>
              Have a great{' '}
              <br className="hidden sm:block" />
              idea?
            </>
          }
          description="We're always looking for ways to improve. Submit your feature request and let the community vote on it."
          actions={
            <Pressable>
              <button
                type="button"
                onClick={handleSubmitClick}
                className={cn('landing-cta landing-cta--solid landing-cta--on-color landing-cta--lg landing-btn-sweep', blueCtaClass)}
              >
                <Plus className="h-5 w-5" />
                Submit your idea
              </button>
            </Pressable>
          }
        />
      </LandingLayout>
    </>
  )
}
